// Orders Legacy Report — closed-only orders with the legacy Mindbody
// column layout: order info + service list (with specialist), subtotal,
// service charge, PPN, grand total, and one column pair per payment
// method used on the order (dynamic width). Footer totals per column
// AND per payment method.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";
// IMMUTABLE billing brain — rate labels ("Service Charge (10%)",
// "PPN (11%)") are DYNAMIC per branch. The engine formats them from
// the backend's `service_charge_rate_pct` / `tax_rate_pct`. No branch
// hardcoding survives this file. See SYSTEM_RULES.md · "Billing
// Engine Isolation".
import { surchargeColumnLabel, taxColumnLabel } from "@/lib/BillingEngine";

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}

export default function OrdersLegacyReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_week");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/orders-legacy`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const maxPayments = data?.max_payments || 0;
  const svcRatePct = Number(data?.service_charge_rate_pct ?? 0);
  const taxRatePct = Number(data?.tax_rate_pct ?? 0);
  const svcChgHeader = surchargeColumnLabel(svcRatePct, "Service Charge");
  const taxHeader = taxColumnLabel(taxRatePct, "PPN");
  const svcChgShort = surchargeColumnLabel(svcRatePct, "Svc Chg");
  const paymentMethodsUsed = useMemo(() => {
    // Ordered list of every method that appears in the payment_totals map
    // so the footer per-method summary reads left-to-right in the order
    // the operators first entered them.
    return Object.keys(data?.payment_totals || {});
  }, [data]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw numbers; PDF keeps the branch label.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const num = { numeric: true, format: money, align: "right" };
    const columns = [
      { header: "Date", key: "date" },
      { header: "Order #", key: "order_number" },
      { header: "Guest", key: "customer" },
      { header: "Services", key: "services" },
      { header: "Specialist", key: "specialists" },
      { header: "Subtotal", key: "subtotal", ...num },
      { header: svcChgHeader, key: "service_charge", ...num },
      { header: taxHeader, key: "ppn", ...num },
      { header: "Grand Total", key: "total", ...num },
    ];
    for (let i = 0; i < maxPayments; i += 1) {
      columns.push({ header: `Payment ${i + 1} Method`, key: `pay_${i}_method` });
      columns.push({ header: `Payment ${i + 1} Amount`, key: `pay_${i}_amount`, ...num });
    }

    const rows = (data.rows || []).map((o) => {
      const row = {
        date: fmtDate(o.date),
        order_number: `#${o.order_number}`,
        customer: o.customer_name,
        services: (o.services || []).map((s) => s.name).join(" | "),
        specialists: (o.services || []).map((s) => s.staff_name || "—").join(" | "),
        subtotal: Number(o.subtotal) || 0,
        service_charge: Number(o.service_charge) || 0,
        ppn: Number(o.ppn) || 0,
        total: Number(o.total) || 0,
      };
      for (let i = 0; i < maxPayments; i += 1) {
        const p = (o.payments || [])[i];
        row[`pay_${i}_method`] = p?.method_name || "";
        row[`pay_${i}_amount`] = p ? (Number(p.amount) || 0) : "";
      }
      return row;
    });

    // Footer: totals per numeric column + per-method payment totals slotted
    // into the same column positions the individual orders use.
    const footer = [
      `Totals · ${data.count} order${data.count === 1 ? "" : "s"}`,
      "", "", "", "",
      Number(data.totals?.subtotal) || 0,
      Number(data.totals?.service_charge) || 0,
      Number(data.totals?.ppn) || 0,
      Number(data.totals?.total) || 0,
    ];
    for (let i = 0; i < maxPayments; i += 1) {
      const method = paymentMethodsUsed[i] || "";
      footer.push(method);
      footer.push(method ? (Number(data.payment_totals[method]) || 0) : "");
    }

    return {
      filename: "orders-legacy-report",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns,
      rows,
      footerRow: footer,
    };
  }, [data, branch, maxPayments, paymentMethodsUsed, svcChgHeader, taxHeader]);

  return (
    <ReportShell
      title="Order Legacy"
      subtitle="Closed orders only · matches the legacy Mindbody column layout."
      branchName={branch?.name}
      tab="sales"
      exportSpec={exportSpec}
    >
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="orders-legacy-report-card">
          <CardContent className="p-0 overflow-x-auto">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">Order Legacy</h2>
              </div>
              {data && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {data.count} closed order{data.count === 1 ? "" : "s"} · grand total
                  </div>
                  <div className="text-xl font-semibold" data-testid="orders-legacy-grand-total">
                    {formatBranchMoney(branch, data.totals?.total || 0)}
                  </div>
                </div>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider">Date</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Order #</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Guest</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Services</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Specialist</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Subtotal</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right" data-testid="orders-legacy-hdr-svc-chg">{svcChgShort}</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right" data-testid="orders-legacy-hdr-ppn">{taxHeader}</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Grand Total</TableHead>
                  {Array.from({ length: maxPayments }).map((_, i) => (
                    <React.Fragment key={i}>
                      <TableHead className="uppercase text-[10px] tracking-wider" data-testid={`orders-legacy-hdr-pay-${i}-method`}>
                        Payment {i + 1}
                      </TableHead>
                      <TableHead className="uppercase text-[10px] tracking-wider text-right" data-testid={`orders-legacy-hdr-pay-${i}-amount`}>
                        Amount
                      </TableHead>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || data.count === 0) ? (
                  <TableRow>
                    <TableCell colSpan={9 + maxPayments * 2} className="text-center text-sm text-muted-foreground py-8">
                      {loading ? "Loading…" : "No closed orders in this range."}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {data.rows.map((o) => (
                      <TableRow key={o.id} data-testid={`orders-legacy-row-${o.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{fmtDate(o.date)}</TableCell>
                        <TableCell className="text-sm font-medium">
                          <Link
                            to={`/staff/branch/${bid}/orders?order=${o.id}`}
                            className="text-primary hover:underline"
                            data-testid={`orders-legacy-open-${o.id}`}
                          >
                            #{o.order_number}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">
                          {o.customer_id ? (
                            <Link to={`/staff/brand/customers/${o.customer_id}`} className="text-primary hover:underline">
                              {o.customer_name}
                            </Link>
                          ) : o.customer_name}
                        </TableCell>
                        <TableCell className="text-xs min-w-[180px]" data-testid={`orders-legacy-cell-${o.id}-services`}>
                          <ul className="space-y-0.5">
                            {(o.services || []).map((s, i) => (
                              <li key={i} className="font-semibold tracking-tight">{s.name}</li>
                            ))}
                          </ul>
                        </TableCell>
                        <TableCell className="text-xs min-w-[140px]" data-testid={`orders-legacy-cell-${o.id}-specialists`}>
                          <ul className="space-y-0.5">
                            {(o.services || []).map((s, i) => (
                              <li key={i} className={s.staff_name ? "" : "text-muted-foreground"}>
                                {s.staff_name || "—"}
                              </li>
                            ))}
                          </ul>
                        </TableCell>
                        <TableCell className="text-right text-sm">{formatBranchMoney(branch, o.subtotal)}</TableCell>
                        <TableCell className="text-right text-sm">{formatBranchMoney(branch, o.service_charge)}</TableCell>
                        <TableCell className="text-right text-sm">{formatBranchMoney(branch, o.ppn)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatBranchMoney(branch, o.total)}</TableCell>
                        {Array.from({ length: maxPayments }).map((_, i) => {
                          const p = (o.payments || [])[i];
                          return (
                            <React.Fragment key={i}>
                              <TableCell className="text-xs" data-testid={`orders-legacy-cell-${o.id}-pay-${i}-method`}>
                                {p ? p.method_name : ""}
                              </TableCell>
                              <TableCell className="text-right text-sm" data-testid={`orders-legacy-cell-${o.id}-pay-${i}-amount`}>
                                {p ? formatBranchMoney(branch, p.amount) : ""}
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                    ))}
                    {/* Footer totals row — always rendered when there are orders. */}
                    <TableRow className="bg-muted/40 font-semibold" data-testid="orders-legacy-totals-row">
                      <TableCell className="text-xs" colSpan={5}>
                        Totals · {data.count} order{data.count === 1 ? "" : "s"}
                      </TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.subtotal || 0)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.service_charge || 0)}</TableCell>
                      <TableCell className="text-right text-sm">{formatBranchMoney(branch, data.totals?.ppn || 0)}</TableCell>
                      <TableCell className="text-right">{formatBranchMoney(branch, data.totals?.total || 0)}</TableCell>
                      {Array.from({ length: maxPayments }).map((_, i) => {
                        const method = paymentMethodsUsed[i];
                        return (
                          <React.Fragment key={i}>
                            <TableCell className="text-xs" data-testid={`orders-legacy-total-pay-${i}-method`}>
                              {method || ""}
                            </TableCell>
                            <TableCell className="text-right text-sm" data-testid={`orders-legacy-total-pay-${i}-amount`}>
                              {method ? formatBranchMoney(branch, data.payment_totals[method]) : ""}
                            </TableCell>
                          </React.Fragment>
                        );
                      })}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
